// Centralized language management for the Koob apps.
//
// The apps rely on the app-level localization (i18next) rather than the
// browser's automatic translation. This module is the single source of truth
// for:
//   - which languages we maintain (SUPPORTED_LANGUAGES),
//   - how we resolve the user's preferred language (explicit choice > profile
//     > browser preference > default),
//   - applying that language to i18next AND to the <html lang> attribute so the
//     browser only offers translation for languages we do NOT maintain.
//
// Keep this framework-agnostic (no React) so both apps and @koob/ui can use it.

export const SUPPORTED_LANGUAGES = ['en', 'fr'];
export const DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_STORAGE_KEY = 'koob-language';

// Normalize e.g. 'fr-FR' / 'FR' -> 'fr' and validate against the supported set.
// Returns null for anything we do not maintain, so callers can fall through.
export const normalizeLanguage = (lng) => {
  if (!lng || typeof lng !== 'string') {
    return null;
  }
  const base = lng.toLowerCase().split('-')[0].trim();
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
};

export const getStoredLanguage = () => {
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    // localStorage can throw in private mode / non-browser contexts.
    return null;
  }
};

export const storeLanguage = (lng) => {
  const normalized = normalizeLanguage(lng);
  if (!normalized) {
    return;
  }
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // ignore write failures (private mode / SSR)
  }
};

// First supported language among the browser's preferences, or null.
export const detectNavigatorLanguage = () => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const candidates = [...(navigator.languages || []), navigator.language];
  for (const candidate of candidates) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

// Language to use before the authenticated user is known (app bootstrap).
// Priority: explicit stored choice > browser preference > default.
export const detectInitialLanguage = () => {
  return getStoredLanguage() || detectNavigatorLanguage() || DEFAULT_LANGUAGE;
};

// Preferred language once we know the user. An explicit stored choice always
// wins (the user overrode their profile), otherwise fall back to the profile
// language, then the browser, then the default.
// Priority: stored choice > user profile > browser preference > default.
export const getPreferredLanguage = ({ userLanguage } = {}) => {
  return (
    getStoredLanguage() ||
    normalizeLanguage(userLanguage) ||
    detectNavigatorLanguage() ||
    DEFAULT_LANGUAGE
  );
};

// Keep <html lang> in sync with the active UI language. This is what makes the
// browser stop offering translation for languages we support, while still
// offering it for languages we don't (graceful degradation), and it improves
// accessibility.
export const syncDocumentLanguage = (lng) => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = normalizeLanguage(lng) || DEFAULT_LANGUAGE;
  }
};

// Single entry point to switch the UI language: updates i18next, syncs
// <html lang>, and optionally persists the choice (only when the user picks it
// explicitly, so an explicit choice keeps winning over profile/browser).
export const applyLanguage = (i18n, lng, { persist = false } = {}) => {
  const normalized = normalizeLanguage(lng) || DEFAULT_LANGUAGE;
  const changePromise =
    i18n && i18n.language !== normalized
      ? i18n.changeLanguage(normalized)
      : Promise.resolve();
  syncDocumentLanguage(normalized);
  if (persist) {
    storeLanguage(normalized);
  }
  return changePromise.then(() => normalized);
};
